import React, { useCallback, useMemo } from 'react'
import { format } from '@e3dc-react/shell/Libs/date'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Header } from '@e3dc-react/shell/Elements/Table'
import { useLocalizeNumberValue } from '@e3dc-react/shell/Hooks/useLocalizeNumberValue'
import { WallboxCell } from '../components/WallboxCell'
import { AuthenticationInfo } from '../components/AuthenticationInfo'
import { ChargingSessionModel } from '../models/charging-sessions.model'
import { ChargingSessionStatusChip } from '../components/ChargingSessionStatusChip'
import { ChargingSessionStatus } from '../types'
import { chargingSessionStatusTranslationKeys } from '../components/charging-session-status-translation-keys'
import { MidMeterIcons } from '../components/MidMeterIcons'
import { getCurrencyValue } from '../../shared/utils/currency-value'
import { getLocalizedNumber } from '../../shared/utils/localized-number'
import { wallboxSortFn } from '../../shared/utils/sort-wallboxes'
import { authenticationSortFn } from '../../shared'

interface UseChargingSessionTableHeader {
  shouldDisplayMidMeter: boolean
}

/**
 * Charging sessions table component visible on desktop screen/view >= lg
 *
 * Shows table columns and triggers charging session details popup on row click.
 */
export const useChargingSessionTableHeader = ({ shouldDisplayMidMeter }: UseChargingSessionTableHeader): Header<ChargingSessionModel>[] => {
  const { t } = useCustomTranslation()
  const localizeNumberValue = useLocalizeNumberValue()

  /** solves sonarqube issue of anonymous function being recreated on each render inside "valueRender" */
  const renderMIDMeterInfoCell = useCallback((midInfo: ChargingSessionModel['midInfo']) => {
    return (
      <MidMeterIcons
        midMeterConnected={midInfo.midMeterConnected}
        midEnergyInconsistent={midInfo.midEnergyInconsistent}
        midSessionOffline={midInfo.midSessionOffline}
      />
    )
  }, [])

  return useMemo<Header<ChargingSessionModel>[]>(
    () => [
      {
        value: 'wallbox',
        label: t('emobility.chargingSession.wallbox'),
        sortFunction: (wallboxOne: ChargingSessionModel['wallbox'], wallboxTwo: ChargingSessionModel['wallbox']) => {
          const wallboxA = {
            wallboxName: wallboxOne.name,
            wallboxId: wallboxOne.id,
          }

          const wallboxB = {
            wallboxName: wallboxTwo.name,
            wallboxId: wallboxTwo.id,
          }

          return wallboxSortFn(wallboxA, wallboxB)
        },
        valueRender: (wallbox: ChargingSessionModel['wallbox']) => {
          return <WallboxCell wallboxId={wallbox.id} />
        },
      },
      {
        value: 'status',
        label: t('emobility.chargingSession.status'),
        valueRender: (status: ChargingSessionStatus | null) => {
          /* At the moment, data migration frequently introduces "null" statuses, so we temporarily guard against them */
          if (!status) {
            return '-'
          }

          return <ChargingSessionStatusChip status={status} />
        },
        sortFunction: (a: ChargingSessionStatus, b: ChargingSessionStatus) =>
          t(chargingSessionStatusTranslationKeys[a]).localeCompare(t(chargingSessionStatusTranslationKeys[b])),
      },
      {
        value: 'startDateTime',
        valueRender: (startDateTime: string | null) => {
          return startDateTime ? format(new Date(startDateTime), t('common.format.date-fns-date-time')) : '-'
        },
        label: t('emobility.chargingSession.startAt'),
        sortFunction: nullsLast((a: string | null, b: string | null) => (a && b ? a.localeCompare(b) : 0)),
      },
      {
        value: 'durationMilliseconds',
        valueRender: (durationMilliseconds: number) => {
          if (durationMilliseconds < 0) {
            return '-'
          }

          return formatDuration(durationMilliseconds)
        },
        label: t('emobility.chargingSession.duration'),
        sortFunction: (a: number, b: number) => {
          return a - b
        },
      },
      {
        value: 'consumptionKWh',
        valueRender: (consumption: number) => {
          return `${t('common.format.kwh', { value: localizeNumberValue(consumption) })}`
        },
        label: t('emobility.chargingHistory.consumption'),
        sortFunction: (a: number, b: number) => {
          return a - b
        },
      },
      {
        value: 'solarEnergyRatio',
        valueRender: (solarEnergyRatio: number) => {
          return `${t('common.format.percentageValue', { value: getLocalizedNumber(solarEnergyRatio * 100) })}`
        },
        label: t('emobility.chargingSession.solarEnergyRatio'),
        sortFunction: (a: number, b: number) => {
          return a - b
        },
      },
      {
        value: 'totalCost',
        valueRender: (totalCost: number | null, _, item) => {
          if (!item) {
            /** item is never undefined, as it is the source of truth for the whole row */
            return ''
          }

          return getCurrencyValue(totalCost ?? 0, item.currencyCode)
        },
        label: t('emobility.chargingSession.price-euro'),
        sortFunction: (a: number, b: number) => {
          return a - b
        },
      },
      {
        value: 'midInfo',
        label: t('emobility.chargingSession.mid-meter-info'),
        valueRender: renderMIDMeterInfoCell,
        isSortable: true,
        sortFunction: (midInfoA: ChargingSessionModel['midInfo'], midInfoB: ChargingSessionModel['midInfo']) => {
          return Number(midInfoA.midMeterConnected) - Number(midInfoB.midMeterConnected)
        },
        isColumnHidden: !shouldDisplayMidMeter,
      },
      {
        value: 'authentication',
        valueRender: (auth: ChargingSessionModel['authentication']) => {
          return <AuthenticationInfo authentication={auth} rfid={auth.rfid} cardName={auth.cardName} />
        },
        label: t('emobility.chargingSession.authentication'),
        sortFunction: (sessionA: ChargingSessionModel['authentication'], sessionB: ChargingSessionModel['authentication']) =>
          authenticationSortFn(sessionA, sessionB),
      },
    ],
    [t, renderMIDMeterInfoCell, shouldDisplayMidMeter, localizeNumberValue]
  )
}
